import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import { ResponsiveModal, ResponsiveModalHeader, ResponsiveModalBody } from '../ui';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { Label, ErrorText, Text, Heading } from '../ui/Text';

const passwordResetRequestSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email format'),
});

type PasswordResetRequestData = z.infer<typeof passwordResetRequestSchema>;

interface PasswordResetModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function PasswordResetModal({ isOpen, onClose }: PasswordResetModalProps) {
  const { requestPasswordReset } = useAuth();
  const { success, error: showError } = useToast();
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<PasswordResetRequestData>({
    resolver: zodResolver(passwordResetRequestSchema),
  });

  const onSubmit = async (data: PasswordResetRequestData) => {
    try {
      setError(null);
      await requestPasswordReset(data);
      setIsSuccess(true);
      success('Password reset email sent successfully');
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to send password reset email';
      setError(errorMessage);
      showError(errorMessage);
    }
  };

  const handleClose = () => {
    setError(null);
    setIsSuccess(false);
    reset();
    onClose();
  };

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      snapPoints={[0.5, 0.8]}
      defaultSnap={1}
      showHandle={true}
    >
      <ResponsiveModalHeader onClose={handleClose}>
        <Heading level={2} size="lg">
          Reset your password
        </Heading>
      </ResponsiveModalHeader>

      <ResponsiveModalBody>
        {isSuccess ? (
          <div className="space-y-4 text-center">
            <div className="text-4xl mb-4">📧</div>
            <Heading level={3} size="md">
              Check your email
            </Heading>
            <Text variant="muted">
              If an account with that email exists, we've sent you a password reset link.
            </Text>
            <Text variant="muted" size="sm">
              The link will expire in 1 hour for security.
            </Text>
            <Button variant="outline" onClick={handleClose} className="w-full">
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-2">
              <Text variant="muted">
                Enter your email address and we'll send you a link to reset your password.
              </Text>
            </div>

            {error && (
              <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                <ErrorText>{error}</ErrorText>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="reset-email">Email address</Label>
              <Input
                id="reset-email"
                type="email"
                placeholder="Enter your email"
                error={errors.email?.message}
                {...register('email')}
              />
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button type="submit" className="flex-1" isLoading={isSubmitting}>
                Send reset link
              </Button>
            </div>
          </form>
        )}
      </ResponsiveModalBody>
    </ResponsiveModal>
  );
}
